import { z } from 'zod'

const WeekdayZ = z.number().int().min(0).max(6)
const RecurrenceUnitZ = z.enum(['day', 'week', 'month', 'year'])
// Fehlt es, streicht Zod das Ende beim Holen — die Wiederholung liefe wieder endlos.
const RecurrenceEndZ = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('count'), remaining: z.number().int().min(1).max(999) }),
  z.object({ kind: z.literal('date'), date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) }),
])

/**
 * Wertobjekt der Wiederholung — spiegelt RecurrenceRule in db/models.ts (ADR 0021).
 * `fixed` bleibt hier lose (Optionals), weil ein discriminatedUnion nur EIN Objekt je
 * Literal kennt; die Engine liest die Felder nach `unit`.
 */
export const RecurrenceRuleZ = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('fixed'),
    interval: z.number().int().min(1),
    unit: RecurrenceUnitZ,
    weekdays: WeekdayZ.array().optional(),
    monthDay: z.number().int().min(1).max(31).optional(),
    // Fehlt es hier, streicht Zod es beim Holen — der 3. Freitag würde „am Tag X“.
    nthWeekday: z.object({ nth: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(-1)]), weekday: WeekdayZ }).optional(),
    yearMonth: z.number().int().min(1).max(12).optional(),
    yearDay: z.number().int().min(1).max(31).optional(),
    end: RecurrenceEndZ.optional(),
  }),
  z.object({ type: z.literal('after_done'), interval: z.number().int().min(1), unit: RecurrenceUnitZ, end: RecurrenceEndZ.optional() }),
  z.object({ type: z.literal('quota'), count: z.number().int().min(1), period: z.enum(['week', 'month', 'year']), end: RecurrenceEndZ.optional() }),
])

/** Validates data coming FROM Supabase (snake_case, ISO strings) */
export const CloudTaskSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  completed: z.boolean(),
  life_space: z.enum(['personal', 'work', 'home']).nullable().optional(),
  project_id: z.string().nullable().optional(),
  section_id: z.string().nullable().optional(),
  order: z.number().nullable().optional().default(0),
  /** Position im Tag auf „Demnächst". Optional, damit ein Gerät mit älterem
   *  Stand (Spalte noch nicht migriert) weiter synchronisiert. */
  day_order: z.number().nullable().optional(),
  tags: z.string().array().default([]),
  attention: z.string().nullable().optional(),
  estimated_duration: z.number().nullable().optional(),
  duration_mode: z.string().nullable().optional(),
  start_date: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  start_period: z.string().nullable().optional(),
  due_date: z.string().nullable().optional(),
  due_time: z.string().nullable().optional(),
  due_period: z.string().nullable().optional(),
  paused_at: z.string().nullable().optional(),
  reward_id: z.string().nullable().optional(),
  recurrence: RecurrenceRuleZ.nullable().optional(),
  local_id: z.string().nullable().optional(),
  imported_from: z.string().nullable().optional(),
  // Elternteil einer Unteraufgabe (T14 #179). Optional: Vor der Migration fehlt die Spalte.
  parent_task_id: z.string().nullable().optional(),
  parent_local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
  favorite_order: z.number().nullable().optional(),
  completed_count: z.number().int().nonnegative().nullable().optional(),
  postponed_count: z.number().int().nonnegative().nullable().optional(),
})

/** Validates data going TO Supabase (subset for create/update) */
export const CloudTaskPayloadSchema = z.object({
  title: z.string().min(1).trim(),
  description: z.string().nullable().optional(),
  completed: z.boolean(),
  life_space: z.enum(['personal', 'work', 'home']),
  project_id: z.string().nullable().optional(),
  section_id: z.string().nullable().optional(),
  order: z.number(),
  day_order: z.number().nullable().optional(),
  tags: z.string().array().optional(),
  attention: z.string().nullable().optional(),
  estimated_duration: z.number().nullable().optional(),
  duration_mode: z.string().nullable().optional(),
  start_date: z.string().nullable().optional(),
  start_time: z.string().nullable().optional(),
  start_period: z.string().nullable().optional(),
  due_date: z.string().nullable().optional(),
  due_time: z.string().nullable().optional(),
  due_period: z.string().nullable().optional(),
  paused_at: z.string().nullable().optional(),
  reward_id: z.string().nullable().optional(),
  recurrence: RecurrenceRuleZ.nullable().optional(),
  local_id: z.string().optional(),
  imported_from: z.string().nullable().optional(),
  parent_task_id: z.string().nullable().optional(),
  parent_local_id: z.string().nullable().optional(),
  /** Nur beim Anlegen einer umgewandelten Unteraufgabe: die Cloud-Kennung, die sie übernimmt. */
  id: z.string().optional(),
  favorite_order: z.number().nullable().optional(),
  completed_count: z.number().int().nonnegative().nullable().optional(),
  postponed_count: z.number().int().nonnegative().nullable().optional(),
})

export type ValidatedCloudTask = z.infer<typeof CloudTaskSchema>
export type ValidatedCloudTaskPayload = z.infer<typeof CloudTaskPayloadSchema>

/** Validates project data coming FROM Supabase */
export const CloudProjectSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.string().optional(), // present in DB but not used during sync
  status: z.enum(['active', 'completed', 'archived']),
  life_space: z.enum(['personal', 'work', 'home']),
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  progress: z.number(),
  order: z.number(),
  local_id: z.string().nullable().optional(),
  imported_from: z.string().nullable().optional(),
  is_system: z.boolean().nullable().optional(),
  system_kind: z.string().nullable().optional(),
  canvas_panel_state: z.enum(['closed', 'split', 'maximized']).nullable().optional(),
  canvas_split_ratio: z.number().nullable().optional(),
  icon: z.string().nullable().optional(),
  icon_color: z.string().nullable().optional(),
  name_html: z.string().nullable().optional(),
  parent_project_id: z.string().nullable().optional(),
  parent_local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
  favorite_order: z.number().nullable().optional(),
})

/** Validates project data going TO Supabase */
export const CloudProjectPayloadSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  status: z.enum(['active', 'completed', 'archived']),
  life_space: z.enum(['personal', 'work', 'home']),
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  progress: z.number(),
  order: z.number(),
  local_id: z.string().optional(),
  imported_from: z.string().nullable().optional(),
  is_system: z.boolean().optional(),
  system_kind: z.string().nullable().optional(),
  canvas_panel_state: z.enum(['closed', 'split', 'maximized']).nullable().optional(),
  canvas_split_ratio: z.number().nullable().optional(),
  icon: z.string().nullable().optional(),
  icon_color: z.string().nullable().optional(),
  name_html: z.string().nullable().optional(),
  parent_project_id: z.string().nullable().optional(),
  parent_local_id: z.string().nullable().optional(),
  favorite_order: z.number().nullable().optional(),
})

export type ValidatedCloudProject = z.infer<typeof CloudProjectSchema>
export type ValidatedCloudProjectPayload = z.infer<typeof CloudProjectPayloadSchema>

/** Validates section data coming FROM Supabase */
export const CloudSectionSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  project_id: z.string(),
  title: z.string(),
  order: z.number(),
  collapsed: z.boolean(),
  local_id: z.string().nullable().optional(),
  imported_from: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  archived_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates section data going TO Supabase */
export const CloudSectionPayloadSchema = z.object({
  project_id: z.string(),
  title: z.string(),
  order: z.number(),
  collapsed: z.boolean(),
  local_id: z.string().optional(),
  imported_from: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  archived_at: z.string().nullable().optional(),
})

export type ValidatedCloudSection = z.infer<typeof CloudSectionSchema>
export type ValidatedCloudSectionPayload = z.infer<typeof CloudSectionPayloadSchema>

/** Validates tag data coming FROM Supabase */
export const CloudTagSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  name: z.string(),
  color: z.string(),
  description: z.string().nullable().optional(),
  usage_count: z.number().int().default(0),
  local_id: z.string().nullable().optional(),
  imported_from: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates tag data going TO Supabase */
export const CloudTagPayloadSchema = z.object({
  name: z.string(),
  color: z.string(),
  description: z.string().nullable().optional(),
  usage_count: z.number().int().optional(),
  local_id: z.string().optional(),
  imported_from: z.string().nullable().optional(),
})

export type ValidatedCloudTag = z.infer<typeof CloudTagSchema>
export type ValidatedCloudTagPayload = z.infer<typeof CloudTagPayloadSchema>

/** Validates goal data coming FROM Supabase */
export const CloudGoalSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  life_space: z.enum(['personal', 'work', 'home']),
  local_id: z.string().nullable().optional(),
  imported_from: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates goal data going TO Supabase */
export const CloudGoalPayloadSchema = z.object({
  title: z.string().trim(),
  description: z.string().nullable().optional(),
  life_space: z.enum(['personal', 'work', 'home']),
  local_id: z.string().optional(),
  imported_from: z.string().nullable().optional(),
})

export type ValidatedCloudGoal = z.infer<typeof CloudGoalSchema>
export type ValidatedCloudGoalPayload = z.infer<typeof CloudGoalPayloadSchema>

// ─── Habit ──────────────────────────────────────────────────────────────────

/** Recurrence value object — mirrors HabitFrequency in db/models.ts. */
export const HabitFrequencyZ = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('daily') }),
  z.object({ kind: z.literal('timeOfDay'), slot: z.enum(['morning', 'noon', 'evening']) }),
  z.object({ kind: z.literal('weekdays'), days: WeekdayZ.array() }),
  z.object({ kind: z.literal('timesPerWeek'), count: z.number().int().min(1) }),
  z.object({ kind: z.literal('monthly'), day: z.number().int().min(1).max(31) }),
  z.object({
    kind: z.literal('interval'),
    every: z.number().int().min(1),
    unit: z.enum(['day', 'week', 'month']),
    weekdays: WeekdayZ.array().optional(),
  }),
])

/** Validates habit data coming FROM Supabase */
export const CloudHabitSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  title: z.string(),
  notes: z.string().nullable().optional(),
  life_space: z.enum(['personal', 'work', 'home']),
  frequency: HabitFrequencyZ,
  start_date: z.string(),
  reward_id: z.string().nullable().optional(),
  target_minutes: z.number().nullable().optional(),
  paused_at: z.string().nullable().optional(),
  sort_order: z.number().default(0),
  local_id: z.string().nullable().optional(),
  imported_from: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates habit data going TO Supabase */
export const CloudHabitPayloadSchema = z.object({
  title: z.string().trim(),
  notes: z.string().nullable().optional(),
  life_space: z.enum(['personal', 'work', 'home']),
  frequency: HabitFrequencyZ,
  start_date: z.string(),
  reward_id: z.string().nullable().optional(),
  target_minutes: z.number().nullable().optional(),
  paused_at: z.string().nullable().optional(),
  sort_order: z.number(),
  local_id: z.string().optional(),
  imported_from: z.string().nullable().optional(),
})

export type ValidatedCloudHabit = z.infer<typeof CloudHabitSchema>
export type ValidatedCloudHabitPayload = z.infer<typeof CloudHabitPayloadSchema>

// ─── Habit log ────────────────────────────────────────────────────────────────

/** Validates habit-log data coming FROM Supabase */
/** Validates subtask data coming FROM Supabase */
export const CloudSubtaskSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  task_id: z.string(),
  task_local_id: z.string().nullable().optional(),
  title: z.string(),
  done: z.boolean(),
  sort_order: z.number(),
  local_id: z.string().nullable().optional(),
  imported_from: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates subtask data going TO Supabase */
export const CloudSubtaskPayloadSchema = z.object({
  task_id: z.string(),
  task_local_id: z.string().optional(),
  title: z.string(),
  done: z.boolean(),
  sort_order: z.number(),
  local_id: z.string().optional(),
  imported_from: z.string().nullable().optional(),
})

export type ValidatedCloudSubtask = z.infer<typeof CloudSubtaskSchema>
export type ValidatedCloudSubtaskPayload = z.infer<typeof CloudSubtaskPayloadSchema>

// ─── Time entry (Fokuszeit) ─────────────────────────────────────────────────

/** Validates time-entry data coming FROM Supabase */
export const CloudTimeEntrySchema = z.object({
  id: z.string(),
  user_id: z.string(),
  task_id: z.string(),
  task_local_id: z.string().nullable().optional(),
  started_at: z.string(),
  // `null` hieße „läuft gerade". Aus der Cloud kommt das praktisch nie: Ein
  // laufender Abschnitt bleibt auf seinem Gerät (ADR 0025).
  ended_at: z.string().nullable().optional(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates time-entry data going TO Supabase */
export const CloudTimeEntryPayloadSchema = z.object({
  task_id: z.string(),
  task_local_id: z.string().optional(),
  started_at: z.string(),
  ended_at: z.string().nullable().optional(),
  local_id: z.string().optional(),
})

export type ValidatedCloudTimeEntry = z.infer<typeof CloudTimeEntrySchema>
export type ValidatedCloudTimeEntryPayload = z.infer<typeof CloudTimeEntryPayloadSchema>

// ─── Task comment (Kommentar, #182) ─────────────────────────────────────────

/** Validates task-comment data coming FROM Supabase */
export const CloudTaskCommentSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  task_id: z.string(),
  task_local_id: z.string().nullable().optional(),
  content: z.string(),
  posted_at: z.string(),
  imported_from: z.string().nullable().optional(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates task-comment data going TO Supabase */
export const CloudTaskCommentPayloadSchema = z.object({
  task_id: z.string(),
  task_local_id: z.string().optional(),
  content: z.string(),
  posted_at: z.string(),
  imported_from: z.string().nullable().optional(),
  local_id: z.string().optional(),
})

export type ValidatedCloudTaskComment = z.infer<typeof CloudTaskCommentSchema>
export type ValidatedCloudTaskCommentPayload = z.infer<typeof CloudTaskCommentPayloadSchema>

// ─── Task reminder (Erinnerung, Plan task-reminders) ────────────────────────

/** `"YYYY-MM-DD"` — Postgres DATE kommt genau so. */
const DayZ = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
/** `"HH:mm"`, Wanduhr. */
const ClockZ = z.string().regex(/^\d{2}:\d{2}$/)

/** Validates task-reminder data coming FROM Supabase */
export const CloudTaskReminderSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  task_id: z.string(),
  task_local_id: z.string().nullable().optional(),
  // Offen für neue Arten (etwa Orte in der Mobil-App): Eine unbekannte überspringt der Pull
  // (`taskReminderPullDescriptor`). Als Schemafehler hielte sie den Wasserstand für immer fest.
  kind: z.string(),
  anchor: z.enum(['start', 'deadline']).nullable().optional(),
  offset_minutes: z.number().int().nonnegative().nullable().optional(),
  remind_date: DayZ.nullable().optional(),
  remind_time: ClockZ.nullable().optional(),
  recurrence: RecurrenceRuleZ.nullable().optional(),
  snoozed_until: z.string().nullable().optional(),
  imported_from: z.string().nullable().optional(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates task-reminder data going TO Supabase — alle Felder, auch die `null`en (Sync-Checkliste Punkt 17). */
export const CloudTaskReminderPayloadSchema = z.object({
  task_id: z.string(),
  task_local_id: z.string().optional(),
  kind: z.enum(['absolute', 'relative']),
  anchor: z.enum(['start', 'deadline']).nullable(),
  offset_minutes: z.number().int().nonnegative().nullable(),
  remind_date: DayZ.nullable(),
  remind_time: ClockZ.nullable(),
  recurrence: RecurrenceRuleZ.nullable(),
  snoozed_until: z.string().nullable(),
  imported_from: z.string().nullable().optional(),
  local_id: z.string().optional(),
})

// ─── Project comment (Kommentar an einem Projekt, #184) ─────────────────────

/** Validates project-comment data coming FROM Supabase */
export const CloudProjectCommentSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  project_id: z.string(),
  project_local_id: z.string().nullable().optional(),
  content: z.string(),
  posted_at: z.string(),
  imported_from: z.string().nullable().optional(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates project-comment data going TO Supabase */
export const CloudProjectCommentPayloadSchema = z.object({
  project_id: z.string(),
  project_local_id: z.string().optional(),
  content: z.string(),
  posted_at: z.string(),
  imported_from: z.string().nullable().optional(),
  local_id: z.string().optional(),
})

export type ValidatedCloudProjectComment = z.infer<typeof CloudProjectCommentSchema>
export type ValidatedCloudProjectCommentPayload = z.infer<typeof CloudProjectCommentPayloadSchema>

// ─── Canvas note (Projekt-Canvas: Notiz/Memo) ───────────────────────────────

/** Validates canvas-note data coming FROM Supabase */
export const CloudCanvasNoteSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  project_id: z.string(),
  project_local_id: z.string().nullable().optional(),
  kind: z.enum(['note', 'memo', 'area', 'text']),
  title: z.string().nullable().optional(),
  content: z.string(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  // Die **Neigung**. `optional`, weil eine Zeile aus einer älteren Fassung sie
  // nicht kennt — sie liegt dann gerade (`?? 0` im Pull).
  rotation: z.number().optional(),
  color: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  show_author: z.boolean().optional(),
  font: z.string().nullable().optional(),
  // Nur beim **Text**. `optional` wie die Neigung: Eine ältere Zeile kennt sie nicht.
  font_size: z.number().nullable().optional(),
  text_align: z.enum(['left', 'center', 'right']).nullable().optional(),
  // **Vorn und hinten**. `optional` wie die Neigung — ohne Wert zählt das Alter.
  z_order: z.number().nullable().optional(),
  area_id: z.string().nullable().optional(),
  area_local_id: z.string().nullable().optional(),
  tape: z.string().nullable().optional(),
  task_id: z.string().nullable().optional(),
  task_local_id: z.string().nullable().optional(),
  converted_at: z.string().nullable().optional(),
  converted_content: z.string().nullable().optional(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates canvas-note data going TO Supabase */
export const CloudCanvasNotePayloadSchema = z.object({
  project_id: z.string(),
  project_local_id: z.string().optional(),
  kind: z.enum(['note', 'memo', 'area', 'text']),
  title: z.string().nullable().optional(),
  content: z.string(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  // Die **Neigung**. `optional`, weil eine Zeile aus einer älteren Fassung sie
  // nicht kennt — sie liegt dann gerade (`?? 0` im Pull).
  rotation: z.number().optional(),
  color: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  show_author: z.boolean().optional(),
  font: z.string().nullable().optional(),
  // Nur beim **Text**. `optional` wie die Neigung: Eine ältere Zeile kennt sie nicht.
  font_size: z.number().nullable().optional(),
  text_align: z.enum(['left', 'center', 'right']).nullable().optional(),
  // **Vorn und hinten**. `optional` wie die Neigung — ohne Wert zählt das Alter.
  z_order: z.number().nullable().optional(),
  area_id: z.string().nullable().optional(),
  area_local_id: z.string().nullable().optional(),
  tape: z.string().nullable().optional(),
  task_id: z.string().nullable().optional(),
  task_local_id: z.string().nullable().optional(),
  converted_at: z.string().nullable().optional(),
  converted_content: z.string().nullable().optional(),
  local_id: z.string().optional(),
})

export type ValidatedCloudCanvasNote = z.infer<typeof CloudCanvasNoteSchema>
export type ValidatedCloudCanvasNotePayload = z.infer<typeof CloudCanvasNotePayloadSchema>

export const CloudHabitLogSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  habit_id: z.string(),
  habit_local_id: z.string().nullable().optional(),
  date: z.string(),
  completed: z.boolean(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates habit-log data going TO Supabase */
export const CloudHabitLogPayloadSchema = z.object({
  habit_id: z.string(),
  habit_local_id: z.string().optional(),
  date: z.string(),
  completed: z.boolean(),
  local_id: z.string().optional(),
})

export type ValidatedCloudHabitLog = z.infer<typeof CloudHabitLogSchema>
export type ValidatedCloudHabitLogPayload = z.infer<typeof CloudHabitLogPayloadSchema>

/** Validates reward-usage data coming FROM Supabase */
export const CloudRewardUsageSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  reward_id: z.string(),
  used_at: z.string(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates reward-usage data going TO Supabase */
export const CloudRewardUsagePayloadSchema = z.object({
  reward_id: z.string(),
  used_at: z.string(),
  local_id: z.string().optional(),
})

export type ValidatedCloudRewardUsage = z.infer<typeof CloudRewardUsageSchema>
export type ValidatedCloudRewardUsagePayload = z.infer<typeof CloudRewardUsagePayloadSchema>

/** Validates preference data coming FROM Supabase */
export const CloudUserPreferenceSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  key: z.string(),
  // jsonb — anything JSON-serialisable, including null. Shape validation is
  // the reading feature's job, not the sync layer's.
  value: z.unknown(),
  local_id: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
})

/** Validates preference data going TO Supabase */
export const CloudUserPreferencePayloadSchema = z.object({
  key: z.string(),
  value: z.unknown(),
  local_id: z.string().optional(),
})

export type ValidatedCloudUserPreference = z.infer<typeof CloudUserPreferenceSchema>
export type ValidatedCloudUserPreferencePayload = z.infer<typeof CloudUserPreferencePayloadSchema>
